/**
 * life/src/routes/dryvn.tsx — Ask Dryvn, the Life face of the Pro
 * dashboard's assistant. Wired to the SAME production brain a text message
 * goes through (POST /api/v1/dryvn/chat → intent/router.routeSms), so a
 * question answered here and one texted to your Dryvn number are literally
 * the same call — multi-turn state included.
 *
 * Adult-only: chat can act (log walks, add bills), which is operational
 * write access. 'viewer' (teen/child) sees a friendly closed door; the
 * server enforces the same line with a 403.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill } from "@/components/dryvn";
import { CollapsibleList } from "@/components/collapsible-list";
import {
  useMe,
  useDryvnHistory,
  useSendDryvn,
  type ChatTurn,
  type ChatAction,
} from "@/lib/queries";
import { IS_DEMO } from "@/lib/mode";

export const Route = createFileRoute("/dryvn")({
  head: () => ({
    meta: [
      { title: "Ask Dryvn — Dryvn Life" },
      { name: "description", content: "Ask your family's Dryvn anything — same brain as texting." },
    ],
  }),
  component: DryvnPage,
});

type Bubble = {
  id: string;
  role: "user" | "assistant";
  text: string;
  actions?: ChatAction[];
  failed?: boolean;
};

function turnsToBubbles(turns: ChatTurn[]): Bubble[] {
  const out: Bubble[] = [];
  for (const t of turns) {
    if (t.message) out.push({ id: `${t.id}-u`, role: "user", text: t.message });
    out.push({
      id: `${t.id}-a`,
      role: "assistant",
      text: t.reply,
      ...(t.actions && t.actions.length > 0 ? { actions: t.actions } : {}),
    });
  }
  return out;
}

/** Prompts, not data — tapping one sends exactly this string. */
const QUICK_ASKS: { id: string; label: string }[] = [
  { id: "qa-bills", label: "What's due this week?" },
  { id: "qa-habits", label: "How are our habits going?" },
  { id: "qa-cal", label: "What's on the family calendar?" },
  { id: "qa-recap", label: "Recap our week" },
];

function DryvnPage() {
  const { data: me } = useMe();
  const isViewer = me?.role === "viewer";
  const { data: turns } = useDryvnHistory(!isViewer && !IS_DEMO);
  const send = useSendDryvn();
  const [live, setLive] = useState<Bubble[]>([]);
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  const bubbles = [...turnsToBubbles(turns), ...live];

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [bubbles.length]);

  const submit = (text: string) => {
    const t = text.trim();
    if (!t || send.isPending) return;
    setDraft("");
    setLive((prev) => [...prev, { id: `live-${Date.now()}-u`, role: "user", text: t }]);
    send.mutate(t, {
      onSuccess: (r) => {
        setLive((prev) => [
          ...prev,
          {
            id: `live-${Date.now()}-a`,
            role: "assistant",
            text: r.reply || "Hmm, I came back empty — try that again?",
            ...(r.actions.length > 0 ? { actions: r.actions } : {}),
          },
        ]);
      },
      onError: () => {
        setLive((prev) => [
          ...prev,
          {
            id: `live-${Date.now()}-a`,
            role: "assistant",
            text: "I couldn't get that through just now — give it another try in a moment.",
            failed: true,
          },
        ]);
      },
    });
  };

  return (
    <AppShell>
      <HeroCard
        eyebrow="Ask Dryvn"
        title={
          <>
            Same{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              Dryvn
            </em>{" "}
            you text.
          </>
        }
      >
        <p className="max-w-md text-sm" style={{ color: "var(--dryvn-grey)" }}>
          One brain, two doors — ask here or by text, and it's the same conversation.
        </p>
      </HeroCard>

      {isViewer ? (
        <DCard className="mx-auto mt-8 max-w-md text-center">
          <Sparkles className="mx-auto h-6 w-6" style={{ color: "var(--dryvn-primary)" }} />
          <p className="mt-3 text-sm" style={{ color: "var(--dryvn-ink)" }}>
            Ask Dryvn is for the adults of the household for now.
          </p>
          <p className="mt-1 text-xs" style={{ color: "var(--dryvn-grey)" }}>
            Your Today, Family, and Health pages are all yours though.
          </p>
        </DCard>
      ) : (
        <div className="mx-auto mt-8 max-w-2xl">
          {/* Quick asks */}
          <div className="mb-4 flex flex-wrap gap-2">
            {QUICK_ASKS.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => submit(q.label)}
                disabled={send.isPending}
                className="rounded-full border px-3 py-1.5 text-xs transition-colors disabled:opacity-50"
                style={{
                  borderColor: "var(--dryvn-line)",
                  backgroundColor: "var(--dryvn-card)",
                  color: "var(--dryvn-ink)",
                }}
              >
                {q.label}
              </button>
            ))}
          </div>

          <DCard className="p-4">
            <div className="max-h-[52vh] space-y-3 overflow-y-auto pr-1">
              {bubbles.length === 0 && (
                <p className="py-8 text-center text-sm" style={{ color: "var(--dryvn-grey)" }}>
                  {IS_DEMO
                    ? "The demo keeps this quiet — your real Dryvn answers at launch."
                    : "Nothing yet — ask about bills, habits, the calendar, or just say what happened today."}
                </p>
              )}
              {/* Tail collapse: a chat reads oldest-first, so the recent slice
                  is the LAST turns and "Show earlier" sits above them. The
                  scroll container is unchanged — this caps what it holds. */}
              <CollapsibleList items={bubbles} resource="messages" tail>
                {(shown) =>
                  shown.map((b) => (
                    <div key={b.id} className={b.role === "user" ? "flex justify-end" : "flex"}>
                      <div
                        className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug"
                        style={
                          b.role === "user"
                            ? { backgroundColor: "var(--dryvn-primary)", color: "#fff" }
                            : {
                                backgroundColor: "var(--dryvn-page-tint)",
                                color: b.failed ? "var(--dryvn-warm-deep)" : "var(--dryvn-ink)",
                              }
                        }
                      >
                        <p className="whitespace-pre-wrap">{b.text}</p>
                        {b.actions && b.actions.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {b.actions.map((a, i) => (
                              <Pill key={`${b.id}-act-${i}`} tone="life">
                                {a.summary}
                              </Pill>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                }
              </CollapsibleList>
              {send.isPending && (
                <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                  Dryvn is thinking…
                </p>
              )}
              <div ref={endRef} />
            </div>

            <form
              className="mt-4 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                submit(draft);
              }}
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Ask anything, or tell Dryvn what happened…"
                className="w-full rounded-full border px-4 py-2.5 text-sm"
                style={{
                  borderColor: "var(--dryvn-line)",
                  backgroundColor: "var(--dryvn-card)",
                  color: "var(--dryvn-ink)",
                }}
              />
              <button
                type="submit"
                disabled={!draft.trim() || send.isPending}
                aria-label="Send"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: "var(--dryvn-primary)" }}
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </DCard>
        </div>
      )}
    </AppShell>
  );
}
